/**
 * Language utility functions for the Gesslar platform
 * Handles language detection, persistence, and specialized terminology
 */

// Define supported languages
export type SupportedLanguage = 'en' | 'es' | 'fr';

// Get user's preferred language from browser or localStorage
export const detectUserLanguage = (): SupportedLanguage => {
  // First check if user has a saved preference
  const savedLanguage = localStorage.getItem('gesslar-language');
  if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'es' || savedLanguage === 'fr')) {
    return savedLanguage;
  }
  
  // Otherwise detect from browser
  const browserLang = navigator.language;
  
  // Map browser language to our supported languages
  if (browserLang.startsWith('es')) {
    return 'es';
  } else if (browserLang.startsWith('fr')) {
    return 'fr';
  } else {
    // Default to English for any other language
    return 'en';
  }
};

// Save user's language preference
export const saveLanguagePreference = (language: SupportedLanguage): void => {
  localStorage.setItem('gesslar-language', language);
};

// Get plant name in the current language and scientific name
export const getPlantName = (
  _plantKey: string, 
  language: SupportedLanguage, 
  includeScientific: boolean = false
): string => {
  // This would typically connect to a plant database
  // For now, we'll return a placeholder
  return `Plant name in ${language}${includeScientific ? ' (Scientific name)' : ''}`;
};

// Types for measurement units
export type MeasurementUnit = 'length' | 'area' | 'volume' | string;

// Format measurements based on user's locale
export const formatMeasurement = (
  value: number, 
  unit: MeasurementUnit, 
  language: SupportedLanguage
): string => {
  // Convert measurements based on locale preferences
  // For example, convert between imperial and metric
  
  const formatter = new Intl.NumberFormat(
    language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'fr-FR'
  );
  
  // Simple conversion example
  if (unit === 'length') {
    if (language === 'en') {
      // Display in feet/inches for English
      return `${formatter.format(value)} ft`;
    } else {
      // Display in meters for other languages
      return `${formatter.format(value * 0.3048)} m`;
    }
  }
  
  // Area measurements
  if (unit === 'area') {
    if (language === 'en') {
      // Display in square feet for English
      return `${formatter.format(value)} sq ft`;
    } else {
      // Display in square meters for other languages
      return `${formatter.format(value * 0.092903)} m²`;
    }
  }
  
  // Volume measurements
  if (unit === 'volume') {
    if (language === 'en') {
      // Display in gallons for English
      return `${formatter.format(value)} gal`;
    } else {
      // Display in liters for other languages
      return `${formatter.format(value * 3.78541)} L`;
    }
  }
  
  // Default formatting
  return `${formatter.format(value)} ${unit}`;
}; 